import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { useEffect, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { PiTrash, PiCheck } from 'react-icons/pi';
import { IBulkAssignVideoContract } from 'services/channel/types/query';
import { useGetContractsByLabel } from 'services/contract';
import { PRODUCT_TYPE } from 'services/contract/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface IContractCardProps {
  contract: IBulkAssignVideoContract & { labelId?: string; labelName?: string };
  index: number;
  labelOptions: Array<{ value: string; label: string }>;
  labelsLoading: boolean;
  percentageLeft: number;
  onUpdate: (updates: Partial<IBulkAssignVideoContract & { labelId?: string; labelName?: string }>) => void;
  onRemove: () => void;
}

const ContractCard = ({
  contract,
  index,
  labelOptions,
  labelsLoading,
  percentageLeft,
  onUpdate,
  onRemove,
}: IContractCardProps) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const previousLabelIdRef = useRef<string | undefined>(contract.labelId);

  const labelContracts = useGetContractsByLabel(contract.labelId || '', {
    params: {
      itemCount: 999,
      page: 0,
      ProductType: PRODUCT_TYPE.YouTube,
      ShowOnlyActive: true,
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!contract.labelId,
  });

  useEffect(() => {
    if (contract.labelId) {
      labelContracts.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.labelId]);

  const contractOptions = useMemo(
    () =>
      labelContracts.data?.pages
        .flat()
        .map(contract => ({ value: contract.id, label: contract.title })) ?? [],
    [labelContracts.data]
  );

  const currentLabelId = contract.labelId || '';

  const labelValue = useMemo(() => {
    if (!currentLabelId) return undefined;
    const foundLabel = labelOptions.find(opt => opt.value === currentLabelId);
    return foundLabel ? { value: foundLabel.value, label: foundLabel.label } : undefined;
  }, [currentLabelId, labelOptions]);

  const contractValue = useMemo(() => {
    if (!contract.contractId || !currentLabelId) return undefined;
    const foundContract = contractOptions.find(opt => opt.value === contract.contractId);
    if (!foundContract) return undefined;
    return { value: foundContract.value, label: foundContract.label };
  }, [contract.contractId, contractOptions, currentLabelId]);

  useEffect(() => {
    if (previousLabelIdRef.current !== undefined && previousLabelIdRef.current !== contract.labelId) {
      setFieldValue(`videoContracts.${index}.contractId`, '');
    }
    previousLabelIdRef.current = contract.labelId;
  }, [contract.labelId, index, setFieldValue]);

  useEffect(() => {
    setFieldValue(`videoContracts.${index}.labelId`, contract.labelId || '');
    setFieldValue(`videoContracts.${index}.contractId`, contract.contractId || '');
    setFieldValue(`videoContracts.${index}.startDate`, contract.startDate || '');
    setFieldValue(`videoContracts.${index}.divisionPercentage`, contract.divisionPercentage ?? 0);
  }, [contract.labelId, contract.contractId, contract.startDate, contract.divisionPercentage, index, setFieldValue]);

  return (
    <div className='p-4 bg-white'>
      <div className='flex gap-4 items-end'>
        <div className='w-[260px]'>
          <FormInput.Select
            name={`videoContracts.${index}.labelId`}
            label={t('input.label.label')}
            isAsync
            isSearchable
            options={labelOptions}
            isLoading={labelsLoading}
            value={labelValue}
            onChange={value => {
              if (value) {
                onUpdate({
                  labelId: String(value.value),
                  labelName: value.label,
                  contractId: '',
                  divisionPercentage: 0,
                });
              } else {
                onUpdate({
                  labelId: '',
                  labelName: '',
                  contractId: '',
                  divisionPercentage: 0,
                });
              }
            }}
          />
        </div>

        <div className='w-[260px]'>
          {currentLabelId ? (
            <FormInput.Select
              key={`contract-${index}-${currentLabelId}`}
              name={`videoContracts.${index}.contractId`}
              label={t('input.label.contract')}
              isAsync
              isSearchable
              options={contractOptions}
              isLoading={labelContracts?.isLoading}
              value={contractValue}
              onChange={value => {
                if (value) {
                  onUpdate({
                    contractId: String(value.value),
                  });
                } else {
                  onUpdate({
                    contractId: '',
                  });
                }
              }}
            />
          ) : (
            <FormInput.Select
              name={`videoContracts.${index}.contractId`}
              label={t('input.label.contract')}
              isDisabled
              options={[]}
            />
          )}
        </div>

        <div className='w-[145px]'>
          <FormInput.Text
            name={`videoContracts.${index}.startDate`}
            type='date'
            label={t('input.label.startDate')}
            value={contract.startDate}
            onChange={e =>
              onUpdate({
                startDate: e.target.value,
              })
            }
          />
        </div>

        <div className='flex gap-2 items-center'>
          <div className='w-32'>
            <FormInput.Number
              name={`videoContracts.${index}.divisionPercentage`}
              label={t('input.label.division')}
              onValueChange={(value, name, values) => {
                onUpdate({
                  divisionPercentage: values?.float || 0,
                });
              }}
              decimalSeparator=','
              groupSeparator=' '
              placeholder={`${percentageLeft} %`}
            />
          </div>
          {(!contract.divisionPercentage || contract.divisionPercentage === 0) && (
            <Button
              size='compact'
              isClear
              onlyIcon
              onClick={() =>
                onUpdate({
                  divisionPercentage: percentageLeft,
                })
              }>
                            <Button.Description description={t('button.apply')} />

                            <Button.Icon icon={<PiCheck />} />
            </Button>
          )}
        </div>

        <Button
          variant={BUTTON_VARIANT.remove}
          isClear
          onlyIcon
          onClick={onRemove}>
                    <Button.Description description={t('button.remove')} />

                    <Button.Icon icon={<PiTrash />} />
        </Button>
      </div>
    </div>
  );
};

export default ContractCard;

